import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bell, User, Settings, CreditCard, HelpCircle, LogOut } from 'lucide-react';
import AuthGuard from '@/components/common/AuthGuard';
import { OnboardingCheck } from '@/components/common/OnboardingCheck';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AmbientPlayer } from '@/components/dashboard/AmbientPlayer';
import { OnboardingTour } from '@/components/dashboard/OnboardingTour';
import { GremlinAssistant } from '@/components/dashboard/GremlinAssistant';

export function TopNavDashboardLayout() {
  console.log('🏗️ TopNavDashboardLayout - Rendering top nav dashboard layout');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const brandName = 'SocialScoop.ai';

  // Fetch avatar URL from profiles table
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });
  }, [user?.id]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  // Get current tab from location
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/dashboard/') return 'dashboard';
    if (path.includes('/feeds')) return 'feeds';
    if (path.includes('/rules') || path.includes('/filtering')) return 'rules';
    if (path.includes('/platforms')) return 'platforms';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/explore')) return 'explore';
    if (path.includes('/settings') || path.includes('/profile') || path.includes('/upgrade') || path.includes('/help')) return 'settings';
    // For pages like ai-lab, tools, listening, moderation - default to dashboard
    return 'dashboard';
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'feeds':
        navigate('/dashboard/feeds');
        break;
      case 'rules':
        navigate('/dashboard/rules');
        break;
      case 'platforms':
        navigate('/dashboard/platforms');
        break;
      case 'analytics':
        navigate('/dashboard/analytics');
        break;
      case 'explore':
        navigate('/dashboard/explore');
        break;
      case 'settings':
        navigate('/dashboard/settings');
        break;
    }
  };

  return (
    <ErrorBoundary>
      <AuthGuard>
        <OnboardingCheck>
          <div className="min-h-screen bg-background font-sans">
            {/* Top Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                  {/* Left side - Brand */}
                  <div className="flex items-center">
                    <span className="text-lg font-semibold tracking-tight text-gray-900">
                      SocialScoop
                    </span>
                  </div>
                  
                  {/* Right side - Notifications, User Menu */}
                  <div className="flex items-center space-x-4">
                    {/* Notifications */}
                    <Button variant="ghost" size="sm" className="relative">
                      <Bell className="h-5 w-5" />
                    </Button>

                    {/* User Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 rounded-full p-0 overflow-hidden bg-blue-100 hover:bg-blue-200"
                        >
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt="Profile"
                              className="h-8 w-8 rounded-full object-cover"
                              onError={() => setAvatarUrl(null)}
                            />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      
                      <DropdownMenuContent className="w-56" align="end" forceMount>
                        <DropdownMenuLabel className="font-normal">
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">
                              {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                          </div>
                        </DropdownMenuLabel>
                        
                        <DropdownMenuSeparator />
                        
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/profile" className="cursor-pointer">
                            <User className="mr-2 h-4 w-4" />
                            <span>Profile</span>
                          </Link>
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/settings" className="cursor-pointer">
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Settings</span>
                          </Link>
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/upgrade" className="cursor-pointer">
                            <CreditCard className="mr-2 h-4 w-4" />
                            <span>Billing</span>
                          </Link>
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/help" className="cursor-pointer">
                            <HelpCircle className="mr-2 h-4 w-4" />
                            <span>Help & Support</span>
                          </Link>
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/tools" className="cursor-pointer">
                            <HelpCircle className="mr-2 h-4 w-4" />
                            <span>Tools</span>
                          </Link>
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        <DropdownMenuItem 
                          className="text-destructive focus:text-destructive cursor-pointer" 
                          onClick={handleSignOut}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>Sign out</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                  </div>
                </div>
              </div>
            </header>

            {/* Navigation Tabs - Always visible */}
            <nav className="bg-white border-b border-gray-200 sticky top-16 z-40">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Tabs value={getCurrentTab()} onValueChange={handleTabChange} className="w-full">
                  <TabsList className="grid w-full grid-cols-7 bg-transparent h-12 p-0">
                    <TabsTrigger
                      value="dashboard"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Home
                    </TabsTrigger>
                    <TabsTrigger
                      value="feeds"
                      data-tour="feeds"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Feeds
                    </TabsTrigger>
                    <TabsTrigger
                      value="rules"
                      data-tour="rules"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Rules
                    </TabsTrigger>
                    <TabsTrigger
                      value="platforms"
                      data-tour="platforms"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Platforms
                    </TabsTrigger>
                    <TabsTrigger
                      value="analytics"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Analytics
                    </TabsTrigger>
                    <TabsTrigger
                      value="explore"
                      data-tour="explore"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Explore
                    </TabsTrigger>
                    <TabsTrigger
                      value="settings"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:text-foreground rounded-none h-full text-muted-foreground hover:text-foreground hover:bg-transparent text-sm font-medium"
                    >
                      Settings
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </nav>
            
            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </main>
            <ErrorBoundary fallback={null}>
              <AmbientPlayer />
            </ErrorBoundary>
            <ErrorBoundary fallback={null}>
              <OnboardingTour />
            </ErrorBoundary>
            <ErrorBoundary fallback={null}>
              <GremlinAssistant />
            </ErrorBoundary>
          </div>
        </OnboardingCheck>
      </AuthGuard>
    </ErrorBoundary>
  );
}

export default TopNavDashboardLayout;